import * as React from 'react'
import PropTypes from 'prop-types'
import TextField from '@mui/material/TextField'
import Autocomplete, { autocompleteClasses } from '@mui/material/Autocomplete'
import useMediaQuery from '@mui/material/useMediaQuery'
import Popper from '@mui/material/Popper'
import { useTheme, styled } from '@mui/material/styles'
import { VariableSizeList } from 'react-window'
import Typography from '@mui/material/Typography'
import { ThemeProvider } from '@mui/material/styles'
import { backYouTheme } from '../../../../theme'
import Label from './Label.jsx'
import Checkbox from '@mui/material/Checkbox'
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank'
import CheckBoxIcon from '@mui/icons-material/CheckBox'
import Skeleton from '@mui/material/Skeleton'

function renderRow(props) {
	const {
		data,
		index,
		style,
		checkbox,
		selectedItems,
		hasGroups,
		autoTrad
	} = props;
	
	const dataSet = data[index];
	const inlineStyle = {
		...style,
		top: style.top + 8,
		paddingLeft: checkbox && 3
	};

	return (
		<Typography component="li" {...dataSet[0]} noWrap style={inlineStyle}>
			<div className={hasGroups ? (!dataSet[1].isGroup ? (dataSet[1].groupId != null ? 'ml-3' : '') : 'font-weight-bold') : ''}>
				{checkbox && <Checkbox
					icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
					checkedIcon={<CheckBoxIcon fontSize="small" />}
					style={{ marginLeft: 0, marginRight: 0 }}
					checked={selectedItems.includes(dataSet[1].value) ? true : false}
				/>}
				{(hasGroups && (dataSet[1].isGroupManager ?? false)) && <i className="fa-solid fa-crown text-gc-dark-blue mr-2" />}
				{autoTrad ? `${dataSet[1].label}`.trad() : dataSet[1].label}
			</div>
		</Typography>
	);
}

const OuterElementContext = React.createContext({});

const OuterElementType = React.forwardRef((props, ref) => {
	const outerProps = React.useContext(OuterElementContext);
	return <div ref={ref} {...props} {...outerProps} />;
});

function useResetCache(data) {
	const ref = React.useRef(null);
	React.useEffect(() => {
		if (ref.current != null) {
			ref.current.resetAfterIndex(0, true);
		}
	}, [data]);
	return ref;
}


// Adapter for react-window
const ListboxComponent = React.forwardRef(function ListboxComponent(props, ref) {

	const { selectedItems, checkbox, children, hasGroups, autoTrad, ...other } = props;
	const itemData = [];
	children.forEach((item) => {
		itemData.push(item);
		itemData.push(...(item.children || []));
	});

	const theme = useTheme();
	const smUp = useMediaQuery(theme.breakpoints.up('sm'), {
		noSsr: true,
	});
	const itemCount = itemData.length;
	const itemSize = smUp ? 36 : 48;

	const getChildSize = (child) => {
		if (child.hasOwnProperty('group')) {
			return 48;
		}

		return itemSize;
	};

	const getHeight = () => {
		if (itemCount > 10) {
			return 10 * itemSize;
		}
		return itemData.map(getChildSize).reduce((a, b) => a + b, 0);
	};

	const gridRef = useResetCache(itemCount);

	return (
		<div ref={ref}>
			<OuterElementContext.Provider value={other}>
				<VariableSizeList
					itemData={itemData}
					height={getHeight() + 2 * 8}
					width="100%"
					ref={gridRef}
					outerElementType={OuterElementType}
					innerElementType="ul"
					itemSize={(index) => getChildSize(itemData[index])}
					overscanCount={5}
					itemCount={itemCount}
				>
					{(props) => renderRow({ ...props, checkbox: checkbox, selectedItems: selectedItems, hasGroups: hasGroups, autoTrad: autoTrad })}
				</VariableSizeList>
			</OuterElementContext.Provider>
		</div>
	);
});


ListboxComponent.propTypes = {
	children: PropTypes.array
};

const StyledPopper = styled(Popper)({
	[`& .${autocompleteClasses.listbox}`]: {
		boxSizing: 'border-box',
		'& ul': {
			padding: 0,
			margin: 0,
		},
	},
});

export default function Select(props) {
  const {
    parentFunction,
    onInputChange = null,
    handleSelectClose = () => {},
    slug,
    label,
    placeHolder = null,
    items = [],
    selectedItems = [],
    autoTrad = false,
    multiple = false,
    clearable = true,
    limitTags = 3,
    checkbox = false,
    isLoading = false,
    error = false,
    classAdded = false,
    id = ("autocomplete-virtualize-" + slug),
    style = 'default',
    hasGroups = false,
    disabledItems = [],
	disabled = false
  } = props;

	let selectedItemsId = [];
	let classDefault = "d-block w-100 bg-white";

	if (style == 'no-style') {
		classDefault = "d-block w-100 border-none";
	}

	if (Array.isArray(selectedItems)) {
		selectedItems.map((e, i) => {
			selectedItemsId.push(e.value)
		})
	}

  return (
    <ThemeProvider theme={backYouTheme}>
      {label && <Label htmlForValue={"select-" + slug} label={label}/>}
      {isLoading ?
        <Skeleton 
          className="card bg-white border border-gc-gray" 
          variant="rectangular" 
          width="100%" 
          height={42} 
        />
        :
        <Autocomplete
          disabled={disabled}
          id={id}
          size="small"
          className={classDefault + " " + classAdded}
          label={''}
          onChange={(event, value) => parentFunction(value, slug)} 
          onInputChange={onInputChange == null ? () => null : (event, value) => onInputChange(value, slug)}
          onClose={() => handleSelectClose()}
          multiple={multiple}
          limitTags={limitTags}
          disableCloseOnSelect={multiple}
          disableClearable={!clearable}
          disableListWrap
          PopperComponent={StyledPopper}
          ListboxComponent={ListboxComponent}
          ListboxProps={{checkbox: checkbox, selectedItems: selectedItemsId, hasGroups: hasGroups, autoTrad:autoTrad}}
          options={items}
          value={selectedItems}
          getOptionLabel={option => (option.label && (autoTrad ? option.label.trad() : option.label))}
          isOptionEqualToValue={(option, value) => option.value == value.value}
          renderInput={(params) => 
            <TextField 
              autoComplete="off" 
              label={placeHolder} 
              error={error}
              {...params} 
            />
          }
          renderOption={(props, option, state) => [props, option, state.index]}
          autoComplete={true}
          selectOnFocus={false}
          getOptionDisabled={(option) => {
            if (disabledItems.includes(option.value)) {
              return true;
            }
          }
        }
        />
      } 
    </ThemeProvider>
  );
}